import { FaqItem } from '../types';
import { db, isFirestoreQuotaExhausted, isQuotaExhaustedError, markFirestoreQuotaExhausted } from './firebase';
import { doc, setDoc, onSnapshot } from 'firebase/firestore';

const FAQ_STORAGE_KEY = 'dojapao_faqs_v1';

export const FAQ_CATEGORIES = [
  'Geral & Suíte',
  'Compras & Personal Shopper',
  'Fretes & Consolidação',
  'Pagamentos & Alfândega',
  'Trocas & Suporte'
];

export const DEFAULT_FAQS: FaqItem[] = [
  {
    id: 'faq-1',
    question: 'O que é um serviço de redirecionamento de encomendas do Japão?',
    answer: 'O redirecionamento do Japão fornece a você um endereço físico em nosso depósito em Saitama, Japão. Ao comprar em lojas japonesas que não entregam diretamente no Brasil (como Mercari JP, Rakuten, Yahoo Auctions e Surugaya), você envia os pacotes para sua Suíte. Nós recebemos, tiramos fotos HD, consolidamos e enviamos com o melhor frete internacional.',
    category: 'Geral & Suíte',
    order: 0,
    createdAt: '2026-01-01T00:00:00.000Z'
  },
  {
    id: 'faq-2',
    question: 'Como comprar no Mercari Japan estando no Brasil?',
    answer: 'O Mercari JP é o maior marketplace de produtos usados, seminovos, figures raras e colecionáveis do Japão. Como o site exige conta japonesa e número de celular local, oferecemos o serviço de Compra Assistida / Personal Shopper: nossa equipe em Saitama faz a compra pelo vendedor japonês, recebe o produto em sua Suíte e prepara para envio ao Brasil.',
    category: 'Compras & Personal Shopper',
    order: 1,
    createdAt: '2026-01-01T00:00:00.000Z'
  },
  {
    id: 'faq-3',
    question: 'Como participar dos leilões no Yahoo Auctions Japan (Yahoo Leilão do Japão)?',
    answer: 'No Yahoo! Auctions Japan você encontra itens antigos, colecionáveis de animes, eletrônicos e relógios com ótimos preços. Através do nosso serviço de Compra Assistida de Leilão do Japão, você nos envia o link do anúncio e o valor máximo de lance desejado. Nossa equipe dá os lances em tempo real no Yahoo Japan e garante sua vitória.',
    category: 'Compras & Personal Shopper',
    order: 2,
    createdAt: '2026-01-01T00:00:00.000Z'
  },
  {
    id: 'faq-4',
    question: 'Quanto custa para abrir uma Suíte no Japão?',
    answer: 'A abertura da sua Suíte é 100% GRATUITA! Não cobramos taxa de adesão, mensalidades ou custos fixos de manutenção. Você tem 60 dias de armazenamento grátis e só paga pelo serviço de envio quando decidir mandar a caixa para o Brasil.',
    category: 'Geral & Suíte',
    order: 3,
    createdAt: '2026-01-01T00:00:00.000Z'
  },
  {
    id: 'faq-5',
    question: 'Como funciona a economia com a Consolidação de Pacotes do Japão?',
    answer: 'O frete internacional do Japão possui um custo fixo inicial. Se você enviar 5 compras separadas do Mercari ou Amazon JP, pagará 5 taxas de frete. Ao consolidar tudo em 1 caixa reforçada, descarte de embalagens secundárias e remoção de espaços vazios, você economiza até 80% no frete.',
    category: 'Fretes & Consolidação',
    order: 4,
    createdAt: '2026-01-01T00:00:00.000Z'
  },
  {
    id: 'faq-6',
    question: 'Quais modalidades de frete do Japão para o Brasil posso escolher?',
    answer: 'Trabalhamos com os métodos oficiais do Japan Post (EMS Express, Airmail Small Packet, SAL Econômico e Surface Marítimo por Navio) e fretes por transportadoras privadas de alta velocidade (Courier FedEx, UPS, DHL) com rastreamento completo até a entrega no seu endereço no Brasil.',
    category: 'Fretes & Consolidação',
    order: 5,
    createdAt: '2026-01-01T00:00:00.000Z'
  },
  {
    id: 'faq-7',
    question: 'Posso declarar o valor dos produtos na alfândega?',
    answer: 'Sim! No painel exclusivo da sua Suíte no site Do Japão Até a Sua Casa, você tem total autonomia para preencher a declaração alfandegária e a discriminação dos itens para envio, cumprindo as exigências dos Correios e da Receita Federal do Brasil.',
    category: 'Pagamentos & Alfândega',
    order: 6,
    createdAt: '2026-01-01T00:00:00.000Z'
  },
  {
    id: 'faq-8',
    question: 'Posso pedir suporte se não souber comprar em sites japoneses?',
    answer: 'Sim! Nosso suporte via WhatsApp orienta você sobre como criar conta em lojas japonesas, preencher o endereço da suíte corretamente e acompanhar seus pedidos com auxílio do Personal Shopper.',
    category: 'Trocas & Suporte',
    order: 7,
    createdAt: '2026-01-01T00:00:00.000Z'
  }
];

export function getStoredFaqs(): FaqItem[] {
  if (typeof window === 'undefined') return DEFAULT_FAQS;
  try {
    const raw = localStorage.getItem(FAQ_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed;
      }
    }
  } catch (e) {
    console.warn('Could not read FAQs from localStorage:', e);
  }
  return DEFAULT_FAQS;
}

export async function saveFaqsToStore(items: FaqItem[]): Promise<void> {
  const normalized = items.map((item, idx) => ({
    ...item,
    order: idx,
    updatedAt: item.updatedAt || new Date().toISOString()
  }));

  if (typeof window !== 'undefined') {
    try {
      localStorage.setItem(FAQ_STORAGE_KEY, JSON.stringify(normalized));
      window.dispatchEvent(new CustomEvent('dojapao:faqs-updated', { detail: normalized }));
    } catch (e) {
      console.warn('Could not save FAQs to localStorage:', e);
    }
  }

  if (!isFirestoreQuotaExhausted) {
    try {
      await setDoc(doc(db, 'settings', 'faq_items'), {
        items: normalized,
        updatedAt: new Date().toISOString()
      });
    } catch (err) {
      if (isQuotaExhaustedError(err)) {
        markFirestoreQuotaExhausted(err);
      } else {
        console.warn('Saved FAQs locally (Firestore sync notice):', err);
      }
    }
  }
}

export function subscribeToFaqs(callback: (items: FaqItem[]) => void): () => void {
  const handleLocalEvent = (e: Event) => {
    const customEvent = e as CustomEvent<FaqItem[]>;
    if (Array.isArray(customEvent.detail)) {
      callback(customEvent.detail);
    }
  };

  if (typeof window !== 'undefined') {
    window.addEventListener('dojapao:faqs-updated', handleLocalEvent);
  }

  let unsubFirestore = () => {};
  if (!isFirestoreQuotaExhausted) {
    try {
      unsubFirestore = onSnapshot(
        doc(db, 'settings', 'faq_items'),
        (docSnap) => {
          if (docSnap.exists()) {
            const data = docSnap.data();
            if (data && Array.isArray(data.items) && data.items.length > 0) {
              if (typeof window !== 'undefined') {
                localStorage.setItem(FAQ_STORAGE_KEY, JSON.stringify(data.items));
              }
              callback(data.items as FaqItem[]);
            }
          }
        },
        (err) => {
          if (isQuotaExhaustedError(err)) {
            markFirestoreQuotaExhausted(err);
          }
        }
      );
    } catch (e) {}
  }

  return () => {
    if (typeof window !== 'undefined') {
      window.removeEventListener('dojapao:faqs-updated', handleLocalEvent);
    }
    unsubFirestore();
  };
}
